import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  authorizeTronEnergyOrder,
  createTronEnergyOrder,
  getCreateTronEnergyOrderChallenge,
  getTronEnergyQuote,
  type TronEnergyConfig,
  type TronEnergyCostEstimate,
} from '@/api';
import { useWalletSignature } from '@/app/hooks/useWalletSignature';
import { isValidTronAddress } from '@/utils/tronAddress';
import { formatCompactNumber, gteUsdt, ltUsdt } from '@/utils/money';

export interface UseTronEnergyOrderFormProps {
  appId: string;
  config?: TronEnergyConfig | null;
  onOrderCreated?: () => void;
}

const DEFAULT_COST_ESTIMATE: TronEnergyCostEstimate = {
  requiredPoints: 0,
  pointsToCharge: 0,
  usdtToCharge: '0',
  affordable: false,
};

export function useTronEnergyOrderForm({
  appId,
  config,
  onOrderCreated,
}: UseTronEnergyOrderFormProps) {
  const { t } = useTranslation();
  const { signMessage } = useWalletSignature();

  const minEnergy = config?.minEnergy || 65000;
  const durationHours = config?.fixedDurationHours ?? 1;

  const [energyAmount, setEnergyAmount] = useState<number>(minEnergy);
  const [rechargeAddress, setRechargeAddress] = useState<string>('');
  const [costEstimate, setCostEstimate] = useState<TronEnergyCostEstimate>(DEFAULT_COST_ESTIMATE);
  const [isQuoteLoading, setIsQuoteLoading] = useState(true);

  const [isSigning, setIsSigning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setEnergyAmount((current) => (ltUsdt(current, minEnergy) ? minEnergy : current));
  }, [minEnergy]);

  const addressValid = useMemo(() => {
    if (!rechargeAddress.trim()) return null;
    return isValidTronAddress(rechargeAddress.trim());
  }, [rechargeAddress]);

  const energyValid = useMemo(() => {
    return gteUsdt(energyAmount, minEnergy);
  }, [energyAmount, minEnergy]);

  useEffect(() => {
    if (!appId || !energyValid) {
      setCostEstimate(DEFAULT_COST_ESTIMATE);
      setIsQuoteLoading(false);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setIsQuoteLoading(true);
      try {
        const res = await getTronEnergyQuote({
          appId,
          energyAmount,
          durationHours,
          receiverAddress: rechargeAddress.trim() || undefined,
        });
        if (!cancelled && res.ok) {
          setCostEstimate(res.data);
        }
      } catch (err) {
        console.error('[TronEnergyOrderForm] Quote error:', err);
      } finally {
        if (!cancelled) {
          setIsQuoteLoading(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [appId, energyAmount, durationHours, rechargeAddress, energyValid]);

  const canSubmit = useMemo(() => {
    return (
      Boolean(rechargeAddress.trim()) &&
      addressValid === true &&
      energyValid &&
      costEstimate.affordable &&
      !isSigning &&
      !isSubmitting &&
      !isQuoteLoading
    );
  }, [
    rechargeAddress,
    addressValid,
    energyValid,
    costEstimate.affordable,
    isSigning,
    isSubmitting,
    isQuoteLoading,
  ]);

  const handleSubmit = async () => {
    if (!appId) return;

    if (!rechargeAddress.trim()) {
      toast.error(t('tronEnergy.form.addressRequired', '请输入能量充值地址'));
      return;
    }

    if (!isValidTronAddress(rechargeAddress.trim())) {
      toast.error(t('tronEnergy.form.invalidAddress', '请输入有效的 Tron Base58Check 地址'));
      return;
    }

    if (!energyValid) {
      toast.error(
        t('tronEnergy.form.minEnergyError', '租用能量需不小于 {{min}}', {
          min: formatCompactNumber(minEnergy),
        }),
      );
      return;
    }

    if (!costEstimate.affordable) {
      toast.error(t('tronEnergy.form.insufficientBalance', '需要补足的 USDT 超过账户余额，无法完成租用'));
      return;
    }

    setIsSigning(true);
    toast.loading(t('tronEnergy.form.waitingSignature', '等待钱包签名...'), {
      id: 'create-tron-order',
    });

    try {
      const challengeRes = await getCreateTronEnergyOrderChallenge({
        appId,
        receiverAddress: rechargeAddress.trim(),
        energyAmount,
        durationHours,
      });
      if (!challengeRes.ok) {
        toast.error(t('tronEnergy.form.challengeFailed', '获取签名挑战失败'), {
          id: 'create-tron-order',
        });
        setIsSigning(false);
        return;
      }

      const signatureResult = await signMessage(challengeRes.data.message);
      if (!signatureResult) {
        toast.error(t('tronEnergy.form.signatureRejected', '已取消签名或签名失败'), {
          id: 'create-tron-order',
        });
        setIsSigning(false);
        return;
      }

      const authorizeRes = await authorizeTronEnergyOrder({
        appId,
        challengeId: challengeRes.data.challengeId,
        signature: signatureResult.signature,
      });
      if (!authorizeRes.ok) {
        toast.error(t('tronEnergy.form.challengeFailed', '获取签名挑战失败'), {
          id: 'create-tron-order',
        });
        setIsSigning(false);
        return;
      }

      setIsSigning(false);
      setIsSubmitting(true);
      toast.loading(t('tronEnergy.form.submittingOrder', '正在提交订单...'), {
        id: 'create-tron-order',
      });

      const orderRes = await createTronEnergyOrder({
        appId,
        receiverAddress: rechargeAddress.trim(),
        energyAmount,
        durationHours,
        authorizationId: authorizeRes.data.authorizationId,
      });

      if (!orderRes.ok) {
        toast.error(t('tronEnergy.form.orderFailed', '下单失败，请重试'), {
          id: 'create-tron-order',
        });
        setIsSubmitting(false);
        return;
      }

      toast.success(t('tronEnergy.form.orderSuccess', '能量租用订单创建成功'), {
        id: 'create-tron-order',
      });

      setIsSubmitting(false);
      onOrderCreated?.();
    } catch (err) {
      console.error('[TronEnergyOrderForm] Submit error:', err);
      toast.error(t('tronEnergy.form.orderFailed', '下单失败，请重试'), {
        id: 'create-tron-order',
      });
      setIsSigning(false);
      setIsSubmitting(false);
    }
  };

  return {
    energyAmount,
    durationHours,
    rechargeAddress,
    minEnergy,
    addressValid,
    energyValid,
    costEstimate,
    isQuoteLoading,
    canSubmit,
    isSigning,
    isSubmitting,
    setEnergyAmount,
    setRechargeAddress,
    handleSubmit,
  };
}
